<template>
  <div>
    <PageHeaderBanner>
      <template #title>SDFile変更</template>
    </PageHeaderBanner>
    <MessageChip :display="isMessageChip" level="E" :message="errMessage" />
    <q-form
      autofocus
      greedy
      @submit="isSdfConfirmationFlg = true"
      @reset="resetForm"
      @validation-error="resetError"
    >
      <div class="cmd00720-input-form q-gutter-y-sm">
        <div class="row q-mt-none">
          <div class="col column">
            <label>カタログID</label>
            <p class="q-mb-none q-ml-sm q-mt-xs">
              {{ inputData.catalog_id }}
            </p>
          </div>
        </div>
        <div class="row">
          <div class="col column">
            <label>カテゴリー</label>
            <p class="q-mb-none q-ml-sm q-mt-xs">
              {{ inputData.sup_category }}
            </p>
          </div>
        </div>
        <div class="row q-gutter-sm">
          <div class="col-2 column">
            <label>サプライヤコード</label>
            <p class="q-mb-none q-ml-sm q-mt-xs">
              {{ inputData.supplier_code }}
            </p>
          </div>
          <div class="col column">
            <label>サプライヤ名</label>
            <p class="q-mb-none q-ml-sm q-mt-xs supplier-name">
              {{ inputData.supplier_name }}
            </p>
          </div>
        </div>
        <div class="row">
          <div class="col column">
            <label class="requier">SDFile名</label>
            <q-input
              v-model="inputData.sup_sdfile_name"
              outlined
              clearable
              dense
              style="background: var(--q-white)"
              class="input-area left-input"
              maxlength="200"
              :rules="[
                (val) => isEmpty(val) || $t('validation.required'),
                (val) => !(val.length <= 5) || $t('validation.chara_min_length', { param1: '6' }),
              ]"
              lazy-rules
              @keydown.enter.prevent
            />
          </div>
        </div>
        <div class="row q-gutter-x-md" style="margin-top: 6px">
          <div class="col-2 column">
            <label>品目数</label>
            <p class="q-mb-none q-ml-sm q-mt-xs">
              {{ inputData.item_num ? commaFormat(inputData.item_num) : '0' }}
            </p>
          </div>
          <div class="col-3 column">
            <label>毒劇品目数</label>
            <p class="q-mb-none q-ml-sm q-mt-xs">
              {{ inputData.poison_num !== null ? commaFormat(inputData.poison_num) : '未実施' }}
            </p>
          </div>
          <div class="col-3 column">
            <label>輸入禁止品目数</label>
            <p class="q-mb-none q-ml-sm q-mt-xs">
              {{ inputData.ban_num !== null ? commaFormat(inputData.ban_num) : '未実施' }}
            </p>
          </div>
          <div class="col column">
            <label>法規制チェックエラー品目数</label>
            <p class="q-mb-none q-ml-sm q-mt-xs">
              {{ inputData.special_num !== null ? commaFormat(inputData.special_num) : '未実施' }}
            </p>
          </div>
        </div>
        <div class="row">
          <div class="col column">
            <label>更新日時</label>
            <p class="q-mb-none q-ml-sm q-mt-xs">
              {{ inputData.update_timestamp }}
            </p>
          </div>
        </div>
        <div class="row">
          <div class="col">
            <label>備考</label>
            <q-input
              v-model="inputData.memo"
              outlined
              dense
              type="textarea"
              counter
              rows="3"
              bg-color="white"
              maxlength="200"
              class="q-pb-none"
            />
          </div>
        </div>
        <FormFooterBanner style="position: inherit">
          <template #button-area>
            <div class="row">
              <q-btn
                color="grey-7"
                outline
                label="戻る"
                style="min-width: 120px; background: var(--q-white) !important"
                @click="router.push({ name: 'sdfile' })"
              />
              <div class="col q-gutter-x-xs flex justify-end">
                <q-btn type="submit" color="primary" style="width: 150px" label="変更" />
                <q-btn
                  type="reset"
                  outline
                  style="
                    width: 150px;
                    background: var(--q-white) !important;
                    color: var(--q-delete);
                  "
                  icon="cached"
                  label="リセット"
                />
              </div>
            </div>
          </template>
        </FormFooterBanner>
      </div>
    </q-form>
    <AlertDialog
      :display="isSdfConfirmationFlg"
      level="information"
      title="更新確認"
      :text="['入力された内容で更新します。', 'よろしいですか？']"
      @confirm="updateSdfile()"
      @close="isSdfConfirmationFlg = false"
    />
    <AlertDialog
      :display="isSdfCompleteFlg"
      level="information"
      title="更新完了"
      :text="['SDFileの更新が完了しました。']"
      confirmation
      @complete="router.push({ name: 'sdfile' })"
    />
    <AlertDialog
      :display="isSdfNotExistFlg"
      level="warning"
      title="データが存在しません"
      :text="['該当のデータが存在しませんでした。', '再度SDFile一覧から選択してください。']"
      confirmation
      @complete="router.push({ name: 'sdfile' })"
    />
  </div>
</template>
<script setup lang="ts">
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { Loading } from 'quasar';
import axios from '../../common/axios-common';
import ResultStatusCode from '../../common/code/ResultStatusCode';
import DetailResultStatusCode from '../../common/code/DetailResultStatusCode';
import HttpResponseStatusCode from '../../common/code/HttpResponseStatusCode';
import commaFormat from '../../common/utils/numberUtils';
import { isEmpty } from '../../common/utils/validateUtils';
import MessageChip from '../../components/chip/MessageChip.vue';
import AlertDialog from '../../components/dialogs/AlertDialog.vue';
import PageHeaderBanner from '../../components/banner/PageHeaderBanner.vue';
import FormFooterBanner from '../../components/banner/FormFooterBanner.vue';

interface Props {
  // カタログID
  catalogId: string;
}

const router = useRouter();
const props = defineProps<Props>();

// MessageChip表示フラグ
const isMessageChip = ref(false);
// エラーメッセージ
const errMessage = ref('');

// SDFile更新確認フラグ
const isSdfConfirmationFlg = ref(false);
// SDFile更新完了フラグ
const isSdfCompleteFlg = ref(false);
// SDFile存在なしフラグ
const isSdfNotExistFlg = ref(false);

// 初期化用データ
const resetData = reactive({
  catalog_id: '',
  sup_category: '',
  supplier_code: '',
  supplier_name: '',
  sup_sdfile_name: '',
  item_num: '',
  poison_num: '',
  ban_num: '',
  special_num: '',
  update_timestamp: '',
  memo: '',
});

/**
 * エラーチップを初期化
 */
const resetError = async () => {
  errMessage.value = '';
  isMessageChip.value = false;
};

// 入力データ
const inputData = ref({ ...resetData });

/**
 * フォーム初期化
 */
const resetForm = () => {
  errMessage.value = '';
  isMessageChip.value = false;
  inputData.value = { ...resetData };
};

/**
 * SDFile情報取得
 */
const getSdfInfo = async () => {
  // Loading Start
  Loading.show();
  // 検索用データ作成
  const formData = new FormData();
  formData.append('catalog_id', props.catalogId);

  // SDFile情報取得
  await axios
    .post('/cmd007201/get-sdf-info', formData)
    .then((res) => {
      const { common, sdfInfo } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          Object.assign(resetData, sdfInfo);
          inputData.value = sdfInfo;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            // SDFileが存在しない場合
            case DetailResultStatusCode.SDF_NOT_EXIST_ERROR:
              isSdfNotExistFlg.value = true;
              break;

            // パラメータエラーの場合
            case DetailResultStatusCode.PARAMETER_ERROR:
            case DetailResultStatusCode.SYSTEM_ERROR:
              router.push({ name: 'error' });
              break;

            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * SDFile更新
 */
const updateSdfile = async () => {
  isSdfConfirmationFlg.value = false;
  errMessage.value = '';
  isMessageChip.value = false;
  // Loading Start
  Loading.show();
  // 更新データ
  const formData = new FormData();
  formData.append('catalog_id', props.catalogId);
  formData.append('sup_sdfile_name', inputData.value.sup_sdfile_name);
  formData.append('memo', inputData.value.memo ? inputData.value.memo : '');

  // SDFile更新
  await axios
    .post('/cmd007201/update-sdf', formData)
    .then((res) => {
      const { common } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          // ダイアログ表示
          isSdfCompleteFlg.value = true;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            // 更新に失敗した場合
            case DetailResultStatusCode.UPDATE_ERROR:
              errMessage.value = common.message;
              isMessageChip.value = true;
              break;

            // パラメータエラーの場合
            case DetailResultStatusCode.PARAMETER_ERROR:
            case DetailResultStatusCode.SYSTEM_ERROR:
              router.push({ name: 'error' });
              break;

            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 * 初期表示処理
 */
// propsが存在しない場合
if (!props.catalogId) {
  // 前画面に遷移
  router.push({ name: 'sdfile' });
} else {
  // SDFile情報取得
  getSdfInfo();
}
</script>
<style scoped>
.cmd00720-input-form {
  width: 70%;
  margin: 0 auto;
}

label {
  letter-spacing: 1px;
  font-size: 0.9rem;
}

.supplier-name {
  white-space: pre-wrap;
  word-break: break-all;
  width: 100%;
}
</style>
<style>
.cmd00720-input-form .q-textarea .q-field__control {
  padding-right: 3px;
}
</style>
